import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { getTourActivityLogs, TourActivityLogResponse } from 'services/tourService';
import { Input } from '../../../ui/input';
import { Badge } from '../../../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { Search, Eye, History, Loader2, Clock, Users, AlertTriangle, BookUser, ArrowUp, ArrowDown, ArrowUpDown, LockKeyhole } from 'lucide-react';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from 'ui/tooltip';
import { useScreenPermission } from 'hooks/useScreenPermission';
import { getVenueApprovalList } from 'services/venueApprovalService';
import useDebounce from 'hooks/useDebounce';
import { FullScreenLoading } from 'components/ui/loader';
import { toast } from 'sonner';
import Pagination from 'ui/pagination';
import { usePageHeader } from 'contexts/PageHeaderContext';

interface VenueApprovalListPageProps {
  onNavigate: (page: string, tourId?: string) => void;
}

interface Validation {
  type: "info" | "warning" | "error";
  message: string;
}

interface VenueApprovalApiItem {
  tourId: number;
  publicId: string;
  tourCode: string;
  tourName: string;
  officeName: string;
  arrivalDate: string;
  departureDate: string;
  duration: number;
  applicants: number;
  tourType: number;
  tourTypeName: string;
  status: string;
  isLocked: boolean;
  validations: Validation[];
  venueSchedules: {
    venueId: number;
    venueName: string;
    checkInDate: string;
    checkOutDate: string;
  }[];
}

// Helper function to format dates
const formatDateRange = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startDay = start.getDate();
  const endDay = end.getDate();
  const month = start.toLocaleDateString('en-US', { month: 'short' });

  return `${startDay}–${endDay} ${month}`;
};

// Helper function to calculate total nights (duration)
const calculateDuration = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export function VenueApprovalListPage({ onNavigate }: VenueApprovalListPageProps) {
  const { setPageHeader } = usePageHeader();
  const { screenName } = useScreenPermission();
  const [loading, setLoading] = useState(false);
  const [activityLogTour, setActivityLogTour] = useState<VenueApprovalApiItem | null>(null);
  const [activityLogData, setActivityLogData] = useState<TourActivityLogResponse | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(false);

  // Fetch activity logs when a tour is selected
  useEffect(() => {
    if (!activityLogTour) return;
    let cancelled = false;
    setActivityLogData(null);
    setActivityLoading(true);
    setActivityError(false);

    getTourActivityLogs(activityLogTour.publicId)
      .then((data) => {
        if (!cancelled) setActivityLogData(data);
      })
      .catch(() => {
        if (!cancelled) setActivityError(true);
      })
      .finally(() => {
        if (!cancelled) setActivityLoading(false);
      });

    return () => { cancelled = true; };
  }, [activityLogTour]);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [venueApprovalList, setVenueApprovallist] = useState([])
  const [venueApprovalTotalCount, setVenueApprovalTotalCount] = useState(0)


  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      searchTerm,
      statusFilter,
      typeFilter
    }),
    [filters, searchTerm, statusFilter, typeFilter],
  );

  const debouncedFilters = useDebounce(mergedFilters, 400);


  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <BookUser className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, setPageHeader]);


  const handleViewTour = (publicId: string) => {
    onNavigate(`/venue-approval/view/${publicId}`);
  };


  // USEEFFECT LISTING DATA FUNCTION START
  const fetchVenueApproval = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVenueApprovalList(debouncedFilters);
      setVenueApprovallist(data?.items || []);
      setVenueApprovalTotalCount(data?.totalCount || 0);
    } catch {
      toast.error("Failed to fetch draft visa applications");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchVenueApproval()
  }, [fetchVenueApproval]);
  // // USEEFFECT FUNCTION END


  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };


  const sortedVenueApplicants = useMemo(() => {
    if (!sortColumn) return venueApprovalList;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...venueApprovalList].sort((a: any, b: any) => {
      const aValue = String(a[sortColumn] ?? "");
      const bValue = String(b[sortColumn] ?? "");

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true, sensitivity: "base" }
      );

      return sortDirection === "asc"
        ? comparison
        : -comparison;
    });
  }, [venueApprovalList, sortColumn, sortDirection]);


  const getValidationIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "error":
        return (
          <AlertTriangle
            className="h-4 w-4 text-red-600 flex-shrink-0"
            strokeWidth={2}
          />
        );
      case "warning":
        return (
          <AlertTriangle
            className="h-4 w-4 text-yellow-600 flex-shrink-0"
            strokeWidth={2}
          />
        );
      case "info":
        return (
          <AlertTriangle
            className="h-4 w-4 text-green-600 flex-shrink-0"
            strokeWidth={2}
          />
        );
      default:
        return null;
    }
  };


  const getTourTypeBadgeClass = (tourTypeName: string) => {
    switch (tourTypeName?.toLowerCase()) {
      case "faiz":
        return "bg-purple-100 text-purple-700 border-purple-300 w-fit";

      case "hotel":
        return "bg-blue-100 text-blue-700 border-blue-300 w-fit";

      case "miqaat":
        return "bg-green-100 text-green-700 border-green-300 w-fit";

      case "instant":
        return "bg-orange-100 text-orange-700 border-orange-300 w-fit";

      case "external":
        return "bg-pink-100 text-pink-700 border-pink-300 w-fit";

      default:
        return "bg-gray-100 text-gray-700 border-gray-300 w-fit";
    }
  };


  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Draft':
        return "bg-gray-100 text-gray-700 border-gray-300";

      case 'Submitted':
        return "bg-blue-100 text-blue-700 border-blue-300";

      case 'Under Review':
        return "bg-yellow-100 text-yellow-700 border-yellow-300";

      case 'Alternative Dates Proposed':
        return "bg-orange-100 text-orange-700 border-orange-300";

      case 'Resubmitted':
        return "bg-indigo-100 text-indigo-700 border-indigo-300";

      case 'Confirmed':
        return "bg-green-100 text-green-700 border-green-300";

      case 'Cancelled':
        return "bg-red-100 text-red-700 border-red-300";

      case 'Checked In':
        return "bg-cyan-100 text-cyan-700 border-cyan-300";

      case 'Checked Out':
        return "bg-purple-100 text-purple-700 border-purple-300";

      case 'Completed':
        return "bg-emerald-100 text-emerald-700 border-emerald-300";

      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };


  const getVenueDateRange = (
    venueSchedules: {
      venueName: string;
      checkInDate: string;
      checkOutDate: string;
    }[],
    venueName: string
  ) => {
    const venue = venueSchedules.find(v => v.venueName.toLowerCase() === venueName.toLowerCase());

    if (!venue) return "N/A";
    const start = new Date(venue.checkInDate);
    const end = new Date(venue.checkOutDate);

    return `${start.getDate()}–${end.getDate()} ${start.toLocaleDateString("en-US", { month: "short" })}`;

  };


  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatActivityLogDate = (date: string) => {
    const d = new Date(date);

    const datePart = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const timePart = d
      .toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    return `${datePart}, ${timePart}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {loading && <FullScreenLoading />}
      {/* Search and Filters in one row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by tour, TO name, or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={typeFilter} onValueChange={(value) => {
          setTypeFilter(value);
          setFilters((prev) => ({ ...prev, pageNo: 1 }));
        }}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="Tour Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value=" ">All Types</SelectItem>
            <SelectItem value="1">Instant</SelectItem>
            <SelectItem value="2">Miqaat</SelectItem>
            <SelectItem value="3">Faiz</SelectItem>
            <SelectItem value="4">External</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(value) => {
          setStatusFilter(value);
          setFilters((prev) => ({ ...prev, pageNo: 1 }));
        }}>
          <SelectTrigger className="w-full sm:w-[220px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value=" ">All Status</SelectItem>
            <SelectItem value="1">Draft</SelectItem>
            <SelectItem value="2">Submitted</SelectItem>
            <SelectItem value="3">Alternative Dates Proposed</SelectItem>
            <SelectItem value="4">Resubmitted</SelectItem>
            <SelectItem value="5">Confirmed</SelectItem>
            <SelectItem value="6">Cancelled</SelectItem>
            <SelectItem value="7">Checked In</SelectItem>
            <SelectItem value="8">Checked Out</SelectItem>
            <SelectItem value="9">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: '#D6E9F8' }} className="border-b-2 border-blue-300">
                <TableHead className="font-semibold" style={{ color: '#5B9BD5' }} onClick={() => handleSort('tourName')}>
                  Tour
                  <div className="flex items-center gap-1">
                    Code
                    {sortColumn === 'tourName' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="font-semibold" style={{ color: '#5B9BD5' }} onClick={() => handleSort('officeName')}>
                  Office Name
                  <div className="flex items-center gap-1">
                    Code
                    {sortColumn === 'officeName' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="font-semibold" style={{ color: '#5B9BD5' }}>Dates</TableHead>
                <TableHead className="font-semibold" style={{ color: '#5B9BD5' }} onClick={() => handleSort('duration')}>
                  Duration
                  <div className="flex items-center gap-1">
                    Code
                    {sortColumn === 'duration' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="font-semibold" style={{ color: '#5B9BD5' }} onClick={() => handleSort('applicants')}>
                  Applicants
                  <div className="flex items-center gap-1">
                    Code
                    {sortColumn === 'applicants' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </div>
                </TableHead>
                <TableHead className="font-semibold" style={{ color: '#5B9BD5' }}>Type</TableHead>
                <TableHead className="font-semibold" style={{ color: '#5B9BD5' }}>Status</TableHead>
                <TableHead className="font-semibold text-center" style={{ color: '#5B9BD5' }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {venueApprovalList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No tours found matching your search criteria
                  </TableCell>
                </TableRow>
              ) : (
                venueApprovalList && sortedVenueApplicants?.map((tour: VenueApprovalApiItem) => (
                  <TableRow key={tour.tourId} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <span className="font-semibold" style={{ color: '#5B9BD5' }}>{tour?.tourCode}</span>
                          <span className="text-sm text-gray-600">{tour.tourName}</span>
                        </div>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button">
                                {getValidationIcon(tour?.validations[0].type)}
                              </button>
                            </TooltipTrigger>

                            <TooltipContent side="right" align="center">
                              <p className="text-xs">{tour?.validations[0].message}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{tour.officeName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {formatDateRange(tour.arrivalDate, tour.departureDate)}
                        </span>
                        <span className="text-xs text-gray-500">
                          K: {getVenueDateRange(tour.venueSchedules, "Karbala")} •
                          N: {getVenueDateRange(tour.venueSchedules, "Najaf")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {tour?.duration ?
                          <span className="font-medium">{tour?.duration}D</span> :
                          <span className="font-medium">{calculateDuration(tour.arrivalDate, tour.departureDate)}D</span>
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{tour.applicants}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getTourTypeBadgeClass(tour.tourTypeName)}`} style={{
                        width: "8rem",
                        justifyContent: "center",
                      }}>
                        {tour.tourTypeName}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`relative px-6 ${getStatusBadgeClass(tour.status)}`} style={{
                        width: "12rem",
                        justifyContent: "center",
                      }}>
                        {tour.status}
                        {tour.isLocked === true && (
                          <LockKeyhole className="absolute right-2 h-3.5 w-3.5" aria-hidden="true" style={{ color: 'red' }} />
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setActivityLogData(null);
                            setActivityError(false);
                            setActivityLoading(true);
                            setActivityLogTour(tour);
                          }}
                          className="h-8 w-8 p-0"
                          title="Activity Log"
                          aria-label={`View activity log for ${tour.tourCode}`}
                        >
                          <History className="w-4 h-4" style={{ color: '#5B9BD5' }} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`View ${tour.tourCode}`}
                          onClick={() => handleViewTour(tour.publicId)}
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="w-4 h-4" style={{ color: '#5B9BD5' }} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* PAGINATION COMPONENT FOR DRAFT */}
          {venueApprovalTotalCount > 0 && <Pagination
            totalItems={venueApprovalTotalCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters((prev) => ({
                ...prev,
                pageNo: page,
                // pageSize: prev.pageSize,
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1,
              }))
            }
            label=""
          />}

        </div>
      </div>
      {/* Activity Log Dialog */}
      <Dialog
        open={!!activityLogTour}
        onOpenChange={(open) => {
          if (!open) setActivityLogTour(null);
        }}
      >
        <DialogContent
          className="bg-white max-h-[90vh] overflow-y-auto"
          style={{ width: "96vw", maxWidth: "1680px" }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#5B9BD5]">
              <History className="h-5 w-5" />
              Activity Log
            </DialogTitle>
            <DialogDescription>
              {activityLogTour
                ? `Process history for ${activityLogData?.tourCode ?? activityLogTour?.tourCode} - ${activityLogTour.tourName}`
                : "Tour process history"}
            </DialogDescription>
          </DialogHeader>

          {activityLogTour && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/60 p-3">
                  <p className="text-xs text-muted-foreground">Tour</p>
                  <p className="font-semibold text-[#1f3d5a]">
                    {activityLogData?.tourCode ?? activityLogTour?.tourCode}
                  </p>
                </div>
                <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/60 p-3">
                  <p className="text-xs text-muted-foreground">Dates</p>
                  <p className="font-semibold text-[#1f3d5a]">
                    {formatDate(activityLogData?.arrivalDate ?? activityLogTour.arrivalDate)} -{" "}
                    {formatDate(activityLogData?.departureDate ?? activityLogTour.departureDate)}
                  </p>
                </div>
                <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/60 p-3">
                  <p className="text-xs text-muted-foreground">
                    Current Status
                  </p>
                  <Badge
                    variant="outline"
                    className="bg-gray-100 text-gray-700 border-gray-300"
                  >
                    {activityLogData?.currentStatusName ?? activityLogTour.status}
                  </Badge>
                </div>
              </div>

              <div className="rounded-xl border border-[#5B9BD5]/25 overflow-hidden">
                <div className="hidden lg:grid grid-cols-[220px_minmax(420px,1fr)_220px_180px] gap-3 bg-[#E3F2FD] px-4 py-3 text-sm font-semibold text-[#1f3d5a]">
                  <span>Process</span>
                  <span>Change Details</span>
                  <span>Date & Time</span>
                  <span>Done By</span>
                </div>

                {activityLoading && (
                  <div role="status" className="flex min-h-[180px] flex-col items-center justify-center gap-3 p-6 text-[#5B9BD5]">
                    <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
                    <span className="text-sm font-medium">Loading activity logs...</span>
                  </div>
                )}
                {activityError && <p role="alert" className="p-6 text-center text-red-600">Failed to load activity logs. Please close this popup and try again.</p>}
                {!activityLoading && !activityError && activityLogData?.activityLogs.length === 0 && (
                  <p className="p-6 text-center text-muted-foreground">No activity logs found for this tour.</p>
                )}
                {activityLogData?.activityLogs.map((log, index) => (
                  <div
                    key={`${log.process}-${index}`}
                    className="grid grid-cols-1 lg:grid-cols-[220px_minmax(420px,1fr)_220px_180px] gap-2 md:gap-3 px-4 py-4 border-t text-sm"
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#5B9BD5]/10 text-[#5B9BD5] font-semibold text-xs">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-semibold text-[#1f3d5a]">
                          {log.process}
                        </p>
                        <Badge
                          variant="outline"
                          className="mt-1 bg-white text-[#5B9BD5] border-[#5B9BD5]/30"
                        >
                          {log.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-muted-foreground md:pt-1">
                      {log.details}
                    </p>
                    <p className="font-medium text-[#1f3d5a] md:pt-1">
                      {formatActivityLogDate(log.dateTime)}
                    </p>
                    <p className="font-medium text-[#1f3d5a] md:pt-1">
                      {log.doneBy}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
